import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Cinematic full-bleed slider for the home hero.
 * Auto-advances every 6s. Manual dots. Persistent overlay text via children.
 */
export default function HeroSlider({ slides = [], interval = 6000, children }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % slides.length), interval);
    return () => clearInterval(t);
  }, [slides.length, interval]);

  return (
    <section className="relative h-screen w-full overflow-hidden bg-charcoal" data-testid="hero-slider">
      <AnimatePresence mode="sync">
        <motion.img
          key={index}
          src={slides[index]?.src}
          alt={slides[index]?.alt || ""}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1.4, ease: "easeInOut" }, scale: { duration: 8, ease: "linear" } }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>

      {/* Tint for text legibility — strengthened */}
      <div className="absolute inset-0 bg-gradient-to-b from-charcoal/75 via-charcoal/55 to-charcoal/85 pointer-events-none" />
      <div className="absolute inset-0 bg-charcoal/35 pointer-events-none" />

      {/* Overlay content */}
      <div className="relative z-10 h-full">{children}</div>

      {/* Dots */}
      {slides.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3" data-testid="hero-slider-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Slide ${i + 1}`}
              data-testid={`hero-dot-${i}`}
              className={`h-[2px] transition-all duration-500 ${
                i === index ? "w-12 bg-ivory" : "w-6 bg-ivory/40 hover:bg-ivory/70"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
